import { ref, computed, onUnmounted } from 'vue';
import { t } from '@/utils/i18n-helper';
import invitacionesService from '@/services/perfil/invitaciones';

// ─── Constants ──────────────────────────────────────────────────────────────

const ERROR_MAP = {
    102: 'Operación no permitida por tipo de tarjeta',
    105: 'Crédito insuficiente',
    108: 'Error en el número de tarjeta',
    109: 'Error en la fecha de caducidad',
    110: 'Error en los datos',
    111: 'Bloque CVC2 incorrecto',
    133: 'Error al procesar el pago, inténtelo de nuevo',
};

const MAX_POLL_ATTEMPTS = 30; // ~90 seconds
const POLL_INTERVAL = 3000;

// ─── State machine ──────────────────────────────────────────────────────────

/**
 * Estados posibles del flujo de pago.
 *   - idle            Sin acción iniciada
 *   - loading         Procesando (tokenizando, llamando API)
 *   - 3ds_challenge   Mostrando iframe 3DS, polling activo
 *   - completed       Pago exitoso
 *   - error           Pago fallido
 */

/**
 * useStepperPago — Estado y operaciones del Paso 5 (pago con Paycomet).
 *
 * Responsabilidades:
 *  - Gestión de la máquina de estados del pago (idle → loading → 3ds_challenge/completed/error).
 *  - Procesamiento del pago via `invitacionesService.procesarPagoPaycomet`.
 *  - Polling de confirmación vía `invitacionesService.checkPedidoProcesado` durante el challenge 3DS.
 *  - Limpieza de intervalos en `onUnmounted`.
 *
 * @param {import('vue').Ref<string>|string} sessionId  ID de sesión del bloqueo activo
 */
export function useStepperPago(sessionId) {

    // ─── State ────────────────────────────────────────────────────────────

    const state     = ref('idle');       // 'idle' | 'loading' | '3ds_challenge' | 'completed' | 'error'
    const iframeUrl = ref('');
    const errorMsg  = ref('');

    /** Resultado de la compra exitosa: { n_pedido, session_id } */
    const resultado = ref(null);

    // ─── Computed ─────────────────────────────────────────────────────────

    const loading = computed(() => state.value === 'loading');

    // ─── Polling internals ────────────────────────────────────────────────

    let pollInterval = null;
    let pollAttempts = 0;

    const stopPolling = () => {
        clearInterval(pollInterval);
        pollInterval = null;
        pollAttempts = 0;
    };

    /**
     * Verifica periódicamente si el pedido ya fue procesado con éxito.
     * Llamado cada POLL_INTERVAL ms mientras el estado es '3ds_challenge'.
     */
    const pollStatus = () => {
        pollAttempts++;

        if (pollAttempts > MAX_POLL_ATTEMPTS) {
            stopPolling();
            state.value = 'error';
            errorMsg.value = t(
                'perfil_invitaciones.pago_error_timeout',
                {},
                'El pago tardó demasiado en confirmarse. Por favor, revisá tu email o contactá con soporte.'
            );
            return;
        }

        const sid = typeof sessionId === 'object' && 'value' in sessionId
            ? sessionId.value
            : sessionId;

        invitacionesService.checkPedidoProcesado(sid).then(res => {
            if (!res.data.error) {
                stopPolling();
                state.value = 'completed';
                resultado.value = {
                    n_pedido: res.data.n_pedido,
                    session_id: sid,
                };
            } else if (res.data.message === 'Pedido fallido') {
                stopPolling();
                state.value = 'error';
                errorMsg.value = t(
                    'perfil_invitaciones.pago_error_default',
                    {},
                    'El pago ha fallado. Inténtalo de nuevo.'
                );
            }
        }).catch(() => {
            if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                stopPolling();
                state.value = 'error';
                errorMsg.value = t(
                    'perfil_invitaciones.pago_procesando',
                    {},
                    'No se pudo verificar el estado del pago. Por favor, revisá tu email o contactá con soporte.'
                );
            }
        });
    };

    // ─── Payment ──────────────────────────────────────────────────────────

    /**
     * Inicia el procesamiento del pago con Paycomet.
     *
     * @param {string} jetToken  Token de pago generado por el iframe de Paycomet
     * @returns {Promise<void>}
     */
    const procesarPago = async jetToken => {
        state.value = 'loading';
        errorMsg.value = '';

        const sid = typeof sessionId === 'object' && 'value' in sessionId
            ? sessionId.value
            : sessionId;

        try {
            const res = await invitacionesService.procesarPagoPaycomet(sid, jetToken);
            const response = res.data.response;

            if (response.challengeUrl) {
                // 3DS challenge requerido
                iframeUrl.value = response.challengeUrl;
                state.value = '3ds_challenge';
                pollAttempts = 0;
                pollInterval = setInterval(pollStatus, POLL_INTERVAL);
            } else if (response.compraFinalizada) {
                // Pago completado directamente
                state.value = 'completed';
                resultado.value = {
                    n_pedido: response.n_pedido,
                    session_id: sid,
                };
            } else {
                // Respuesta inesperada — se trata como error
                state.value = 'error';
                errorMsg.value = t(
                    'perfil_invitaciones.pago_error_default',
                    {},
                    'Error al procesar el pago'
                );
            }
        } catch (error) {
            const code = error.response?.data?.response?.errorCode;
            errorMsg.value = ERROR_MAP[code] ??
                t('perfil_invitaciones.pago_error_default', {}, 'Error al procesar el pago');
            state.value = 'error';
        }
    };

    // ─── Reset ────────────────────────────────────────────────────────────

    /**
     * Reinicia todos los valores a su estado inicial y detiene el polling.
     */
    const reiniciar = () => {
        stopPolling();
        state.value = 'idle';
        iframeUrl.value = '';
        errorMsg.value = '';
        resultado.value = null;
    };

    // ─── Cleanup ──────────────────────────────────────────────────────────

    onUnmounted(() => {
        stopPolling();
    });

    // ─── API ──────────────────────────────────────────────────────────────

    return {
        state,
        loading,
        iframeUrl,
        errorMsg,
        resultado,
        procesarPago,
        reiniciar,
    };
}
